/**
 * Theme Context Provider
 * 
 * Provides theme state and controls throughout the React application.
 * Handles theme persistence, system preference detection, and theme switching.
 */

import React, { createContext, useContext, ReactNode } from 'react';
import { useTheme } from '../hooks/useTheme';
import type { ThemeContextValue } from '../styles/theme';

// Create theme context
const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// Theme provider props
interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Theme Provider Component
 * 
 * Wrap your app with this provider to enable theme management
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const themeValue = useTheme();

  return (
    <ThemeContext.Provider value={themeValue}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Hook to consume theme context
 * 
 * Must be used within a ThemeProvider
 */
const useThemeContext = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  
  return context;
};

/**
 * Theme Toggle Button Component
 * 
 * Ready-to-use button for theme switching
 */
interface ThemeToggleProps {
  className?: string;
  children?: ReactNode;
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({ 
  className = '', 
  children 
}) => {
  const { theme, toggle, isDark } = useThemeContext();

  return (
    <button
      onClick={toggle}
      className={`theme-toggle ${className}`}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      title={`Current theme: ${theme}. Click to switch.`}
    >
      {children || (
        <>
          {isDark ? '☀️' : '🌙'} 
          <span className="sr-only">
            Switch to {isDark ? 'light' : 'dark'} mode
          </span>
        </>
      )}
    </button>
  );
};

/**
 * Theme-aware wrapper component
 * 
 * Automatically applies theme classes and styles
 */
interface ThemeWrapperProps {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export const ThemeWrapper: React.FC<ThemeWrapperProps> = ({ 
  children, 
  className = '', 
  style = {} 
}) => {
  const { theme, colors } = useThemeContext();

  const wrapperStyle: React.CSSProperties = {
    backgroundColor: colors.BG_PRIMARY,
    color: colors.TEXT_PRIMARY,
    minHeight: '100vh',
    transition: 'background-color 0.2s ease, color 0.2s ease',
    ...style,
  };

  return (
    <div 
      className={`theme-wrapper theme-${theme} ${className}`}
      style={wrapperStyle}
      data-theme={theme}
    >
      {children}
    </div>
  );
};

/**
 * Example usage:
 * 
 * // App.tsx
 * import { ThemeProvider, ThemeWrapper } from './contexts/ThemeContext';
 * 
 * function App() {
 *   return (
 *     <ThemeProvider>
 *       <ThemeWrapper>
 *         <Header />
 *         <Main />
 *         <Footer />
 *       </ThemeWrapper>
 *     </ThemeProvider>
 *   );
 * }
 * 
 * // Header.tsx
 * import { ThemeToggle, useThemeContext } from './contexts/ThemeContext';
 * 
 * function Header() {
 *   const { theme, colors } = useThemeContext();
 *   
 *   return (
 *     <header style={{ backgroundColor: colors.BG_SURFACE }}>
 *       <h1>My App</h1>
 *       <ThemeToggle />
 *     </header>
 *   );
 * }
 * 
 * // Component with HOC
 * const ThemedComponent = withTheme<{ title: string }>(({ title, theme }) => (
 *   <div style={{ color: theme.colors.TEXT_PRIMARY }}>
 *     {title} - Current theme: {theme.theme}
 *   </div>
 * ));
 */